/**
 * Owner: Masa Bayu
 * File: library-sync-queue.ts
 * Date:Time: 2026-09-16 06:52
 * Description: T4-E3 offline sync queue — enqueue, retry, idempotent replay.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
import { assertT4E3SecurityIntact } from './library-security';
export const QXREAD_LIBRARY_SYNC_DIRECTIONS = ['local_to_remote', 'remote_to_local'];
export const QXREAD_LIBRARY_SYNC_CHANGE_KINDS = [
    'progress',
    'state',
    'archive',
    'offline_flag',
];
export const QXREAD_LIBRARY_SYNC_CHANGE_STATUSES = [
    'queued',
    'in_flight',
    'acked',
    'failed',
    'dead_letter',
];
export function createEmptyLibrarySyncQueue(accountId) {
    assertValidId(accountId, 'QXRead sync queue accountId');
    assertT4E3SecurityIntact();
    return Object.freeze({
        accountId,
        changes: Object.freeze([]),
        seenIdempotencyKeys: new Set(),
    });
}
export function enqueueLibrarySyncChange(queue, input) {
    assertNoForbiddenFoundationKey(input, 'QXRead sync enqueue');
    assertT4E3SecurityIntact();
    if (queue.accountId !== input.snapshot.accountId) {
        throw new Error('QXRead sync queue accountId mismatch');
    }
    assertValidId(input.idempotencyKey, 'QXRead sync idempotencyKey');
    if (!QXREAD_LIBRARY_SYNC_DIRECTIONS.includes(input.direction)) {
        throw new Error(`QXRead sync direction invalid: ${input.direction}`);
    }
    if (!QXREAD_LIBRARY_SYNC_CHANGE_KINDS.includes(input.kind)) {
        throw new Error(`QXRead sync change kind invalid: ${input.kind}`);
    }
    const existing = queue.changes.find((change) => change.idempotencyKey === input.idempotencyKey);
    if (existing !== undefined || queue.seenIdempotencyKeys.has(input.idempotencyKey)) {
        const change = existing ??
            Object.freeze({
                changeId: input.changeId ?? createQxreadId('qxrdsch'),
                idempotencyKey: input.idempotencyKey,
                accountId: queue.accountId,
                libraryItemId: input.snapshot.libraryItemId,
                direction: input.direction,
                kind: input.kind,
                status: 'acked',
                snapshot: input.snapshot,
                queuedAt: input.queuedAt ?? new Date().toISOString(),
                attemptCount: 0,
                lastAttemptAt: null,
                lastError: null,
            });
        return Object.freeze({ queue, change, duplicated: true });
    }
    const queuedAt = input.queuedAt ?? new Date().toISOString();
    assertTimestamp(queuedAt, 'QXRead sync queuedAt');
    const change = Object.freeze({
        changeId: input.changeId ?? createQxreadId('qxrdsch'),
        idempotencyKey: input.idempotencyKey,
        accountId: queue.accountId,
        libraryItemId: input.snapshot.libraryItemId,
        direction: input.direction,
        kind: input.kind,
        status: 'queued',
        snapshot: input.snapshot,
        queuedAt,
        attemptCount: 0,
        lastAttemptAt: null,
        lastError: null,
    });
    const seen = new Set(queue.seenIdempotencyKeys);
    seen.add(input.idempotencyKey);
    return Object.freeze({
        queue: Object.freeze({
            accountId: queue.accountId,
            changes: Object.freeze([...queue.changes, change]),
            seenIdempotencyKeys: seen,
        }),
        change,
        duplicated: false,
    });
}
export function markLibrarySyncChangeInFlight(queue, changeId, attemptedAt) {
    assertT4E3SecurityIntact();
    const at = attemptedAt ?? new Date().toISOString();
    assertTimestamp(at, 'QXRead sync lastAttemptAt');
    return mapQueueChange(queue, changeId, (change) => {
        if (change.status !== 'queued' && change.status !== 'failed') {
            throw new Error(`QXRead sync in-flight blocked from status "${change.status}"`);
        }
        return Object.freeze({
            ...change,
            status: 'in_flight',
            attemptCount: change.attemptCount + 1,
            lastAttemptAt: at,
            lastError: null,
        });
    });
}
export function ackLibrarySyncChange(queue, changeId) {
    assertT4E3SecurityIntact();
    return mapQueueChange(queue, changeId, (change) => Object.freeze({
        ...change,
        status: 'acked',
        lastError: null,
    }));
}
export function failLibrarySyncChange(queue, changeId, error, options) {
    assertT4E3SecurityIntact();
    if (typeof error !== 'string' || error.trim().length === 0) {
        throw new Error('QXRead sync failure reason is required');
    }
    const maxAttempts = options?.maxAttempts ?? 3;
    return mapQueueChange(queue, changeId, (change) => {
        const dead = change.attemptCount >= maxAttempts;
        return Object.freeze({
            ...change,
            status: dead ? 'dead_letter' : 'failed',
            lastError: error.trim(),
        });
    });
}
export function listRetryableLibrarySyncChanges(queue) {
    return Object.freeze(queue.changes.filter((change) => change.status === 'queued' || change.status === 'failed'));
}
function mapQueueChange(queue, changeId, map) {
    assertValidId(changeId, 'QXRead sync changeId');
    const index = queue.changes.findIndex((change) => change.changeId === changeId);
    if (index < 0) {
        throw new Error(`QXRead sync change not found: ${changeId}`);
    }
    const changes = queue.changes.map((change, changeIndex) => changeIndex === index ? map(change) : change);
    return Object.freeze({
        accountId: queue.accountId,
        changes: Object.freeze(changes),
        seenIdempotencyKeys: queue.seenIdempotencyKeys,
    });
}
/** Queued offline changes never mint entitlements or grant access. */
export function syncQueueCannotGrantAccess(_queue) {
    return true;
}
//# sourceMappingURL=library-sync-queue.js.map